import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { 
  MdInventory,
  MdLocationOn,
  MdSearch,
  MdFilterList,
  MdInfo,
  MdTrendingUp,
  MdCalendarToday,
  MdViewModule,
  MdGridOn,
  MdFactory,
  MdBuild,
  MdLocalShipping,
  MdAttachMoney,
  MdFlag,
  MdCheckCircle,
  MdWarning,
  MdError,
  MdChevronLeft,
  MdChevronRight,
  MdFirstPage,
  MdLastPage,
  MdRefresh,
  MdDelete,
  MdVisibility,
  MdClose
} from "react-icons/md";
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { jwtDecode } from 'jwt-decode';

const ExportRecords = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('latest_export_date');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [exportRecords, setExportRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [deletingRecordId, setDeletingRecordId] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [productDetailsPage, setProductDetailsPage] = useState(1);
  const [productDetailsItemsPerPage, setProductDetailsItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    customer: ''
  });
  const [viewMode, setViewMode] = useState('product'); // 'product' or 'customer'

  // Fetch export records from API
  const fetchExportRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('https://usibras-web-backend.onrender.com/api/export');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.data) {
        setExportRecords(result.data);
      } else {
        setExportRecords([]);
      }
    } catch (err) {
      console.error('Error fetching export records:', err);
      setError('Failed to load export records. Please try again.');
      setExportRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Check user role
  useEffect(() => {
    try {
      const token = localStorage.getItem('usibras_login');
      if (token) {
        const decodedToken = jwtDecode(token);
        setUserRole(decodedToken.role);
      }
    } catch (error) {
      console.error('Error checking user role:', error);
      setUserRole(null);
    }
  }, []);

  // Fetch data on component mount
  useEffect(() => {
    fetchExportRecords();
  }, []);

  // Group records by product type
  const groupRecordsByProductType = (records) => {
    const grouped = {};
    records.forEach(record => {
      if (!grouped[record.product_type]) {
        grouped[record.product_type] = [];
      }
      grouped[record.product_type].push(record);
    });
    return grouped;
  };

  // Calculate statistics for unique product types
  const calculateStats = (records) => {
    const groupedRecords = groupRecordsByProductType(records);
    const uniqueProductTypes = Object.keys(groupedRecords);
    const totalRecords = records.length;
    const totalQuantity = records.reduce((sum, r) => sum + r.quantity, 0);
    const recentExports = records.filter(r => {
      const exportDate = new Date(r.export_date);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return exportDate >= thirtyDaysAgo;
    }).length;

    return {
      totalRecords,
      totalQuantity,
      uniqueProductTypes: uniqueProductTypes.length,
      recentExports
    };
  };

  // Create unique product type records
  const createUniqueProductRecords = (records) => {
    const groupedRecords = groupRecordsByProductType(records);
    return Object.keys(groupedRecords).map(productType => {
      const recordsForProduct = groupedRecords[productType];
      const totalQuantity = recordsForProduct.reduce((sum, r) => sum + r.quantity, 0);
      const latestExport = recordsForProduct.reduce((latest, current) => 
        new Date(current.export_date) > new Date(latest.export_date) ? current : latest
      );
      const earliestStorage = recordsForProduct.reduce((earliest, current) => 
        new Date(current.storage_date) < new Date(earliest.storage_date) ? current : earliest
      );

      return {
        product_type: productType,
        total_records: recordsForProduct.length,
        total_quantity: totalQuantity,
        latest_export_date: latestExport.export_date,
        earliest_storage_date: earliestStorage.storage_date,
        records: recordsForProduct
      };
    });
  };

  // Create unique customer records
  const createUniqueCustomerRecords = (records) => {
    const groupedByCustomer = {};
    records.forEach(record => {
      const customerName = record.customer_name || 'Unknown Customer';
      if (!groupedByCustomer[customerName]) {
        groupedByCustomer[customerName] = [];
      }
      groupedByCustomer[customerName].push(record);
    });

    return Object.keys(groupedByCustomer).map(customerName => {
      const recordsForCustomer = groupedByCustomer[customerName];
      const totalQuantity = recordsForCustomer.reduce((sum, r) => sum + r.quantity, 0);
      const latestExport = recordsForCustomer.reduce((latest, current) => 
        new Date(current.export_date) > new Date(latest.export_date) ? current : latest
      );
      
      // Get unique product types for this customer
      const uniqueProductTypes = [...new Set(recordsForCustomer.map(r => r.product_type))];

      return {
        customer_name: customerName,
        total_records: recordsForCustomer.length,
        total_quantity: totalQuantity,
        unique_product_types: uniqueProductTypes.length,
        product_types: uniqueProductTypes,
        latest_export_date: latestExport.export_date,
        records: recordsForCustomer
      };
    });
  };

  // Filter and sort unique product records
  const uniqueProductRecords = createUniqueProductRecords(exportRecords);
  const filteredProductRecords = uniqueProductRecords
    .filter(record => {
      const matchesSearch = record.product_type.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Date range filter
      const matchesDateRange = (!filters.startDate || new Date(record.latest_export_date) >= new Date(filters.startDate)) &&
                              (!filters.endDate || new Date(record.latest_export_date) <= new Date(filters.endDate));
      
      // Customer filter - check if any record in this product type matches the customer filter
      const matchesCustomer = !filters.customer || record.records.some(r => 
        r.customer_name && r.customer_name.toLowerCase().includes(filters.customer.toLowerCase())
      );
      
      return matchesSearch && matchesDateRange && matchesCustomer;
    })
    .sort((a, b) => {
      return new Date(b.latest_export_date) - new Date(a.latest_export_date);
    });

  // Filter and sort unique customer records
  const uniqueCustomerRecords = createUniqueCustomerRecords(exportRecords);
  const filteredCustomerRecords = uniqueCustomerRecords
    .filter(record => {
      const matchesSearch = record.customer_name.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Date range filter
      const matchesDateRange = (!filters.startDate || new Date(record.latest_export_date) >= new Date(filters.startDate)) &&
                              (!filters.endDate || new Date(record.latest_export_date) <= new Date(filters.endDate));
      
      // Customer filter - direct match for customer view
      const matchesCustomer = !filters.customer || record.customer_name.toLowerCase().includes(filters.customer.toLowerCase());
      
      return matchesSearch && matchesDateRange && matchesCustomer;
    })
    .sort((a, b) => {
      return new Date(b.latest_export_date) - new Date(a.latest_export_date);
    });

  // Get current view data based on view mode
  const currentViewData = viewMode === 'product' ? filteredProductRecords : filteredCustomerRecords;
  
  // Pagination calculations
  const totalPages = Math.ceil(currentViewData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRecords = currentViewData.slice(startIndex, endIndex);

  // Reset to first page when filters or view mode change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, viewMode]);

  // Calculate stats based on filtered data
  const filteredStats = calculateStats(
    currentViewData.flatMap(record => record.records)
  );



  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Handle delete function
  const handleDelete = async (recordId) => {
    try {
      setDeletingRecordId(recordId);
      const response = await axios.delete(`https://usibras-web-backend.onrender.com/api/export/${recordId}`);
      
      if (response.data && response.data.message) {
        toast.success(response.data.message);
        // Remove the deleted record from the local state
        setExportRecords(prevRecords => prevRecords.filter(record => record.id !== recordId));
        // Update the selected product type records
        if (selectedProductType) {
          setSelectedProductType(prev => ({
            ...prev,
            records: prev.records.filter(record => record.id !== recordId),
            total_records: prev.total_records - 1
          }));
        }
      } else {
        toast.error('Failed to delete export record. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting export record:', error);
      toast.error('Failed to delete export record. Please try again.');
    } finally {
      setDeletingRecordId(null);
    }
  };

  // Add confirmation dialog functions
  const openDeleteConfirm = (record) => {
    setRecordToDelete(record);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setRecordToDelete(null);
  };

  const confirmDelete = async () => {
    if (recordToDelete) {
      await handleDelete(recordToDelete.id);
      closeDeleteConfirm();
    }
  };

  // Handle view product details
  const handleViewProduct = (productRecord) => {
    setSelectedProductType(productRecord);
    setShowProductDetails(true);
    setProductDetailsPage(1);
  };

  // Handle view customer details
  const handleViewCustomer = (customerRecord) => {
    setSelectedProductType(customerRecord);
    setShowProductDetails(true);
    setProductDetailsPage(1);
  };

  const closeProductDetails = () => {
    setShowProductDetails(false);
    setSelectedProductType(null);
  };

  // Pagination for product details
  const productDetailsStartIndex = (productDetailsPage - 1) * productDetailsItemsPerPage;
  const productDetailsEndIndex = productDetailsStartIndex + productDetailsItemsPerPage;
  const currentProductDetailsRecords = selectedProductType 
    ? selectedProductType.records.slice(productDetailsStartIndex, productDetailsEndIndex)
    : [];
  const productDetailsTotalPages = selectedProductType 
    ? Math.ceil(selectedProductType.records.length / productDetailsItemsPerPage)
    : 0;

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-4"}>
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
              Export Records {viewMode === 'product' ? 'by Product Type' : 'by Customer'}
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {viewMode === 'product' 
                ? 'View and manage export records grouped by product type'
                : 'View and manage export records grouped by customer'
              }
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex rounded-lg bg-gray-100 p-1 dark:bg-navy-700">
              <button
                onClick={() => setViewMode('product')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  viewMode === 'product'
                    ? 'bg-white text-blue-600 shadow-sm dark:bg-navy-600 dark:text-blue-400'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <MdFactory className="h-4 w-4 inline mr-1" />
                Products
              </button>
              <button
                onClick={() => setViewMode('customer')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  viewMode === 'customer'
                    ? 'bg-white text-blue-600 shadow-sm dark:bg-navy-600 dark:text-blue-400'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <MdFactory className="h-4 w-4 inline mr-1" />
                Customers
              </button>
            </div>
            <button
              onClick={fetchExportRecords}
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50"
            >
              <MdRefresh className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="mb-8 text-center">
            <div className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-4 py-2 text-blue-700 dark:bg-navy-700 dark:text-blue-400">
              <MdRefresh className="h-5 w-5 animate-spin" />
              <span>Loading export records...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-8 rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
            <div className="flex items-center gap-2">
              <MdError className="h-5 w-5 text-red-500" />
              <span className="text-red-700 dark:text-red-400">{error}</span>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {!loading && !error && (
          <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2">
            <Card extra="p-4">
              <div className="flex items-center gap-4">
                <div className="rounded-full bg-green-50 p-3 dark:bg-navy-700">
                  <MdInventory className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Quantity</p>
                  <p className="text-xl font-bold text-navy-700 dark:text-white">{filteredStats.totalQuantity.toLocaleString()} boxes</p>
                </div>
              </div>
            </Card>

            <Card extra="p-4">
              <div className="flex items-center gap-4">
                <div className="rounded-full bg-purple-50 p-3 dark:bg-navy-700">
                  <MdFactory className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {viewMode === 'product' ? 'Product Types' : 'Customers'}
                  </p>
                  <p className="text-xl font-bold text-navy-700 dark:text-white">{currentViewData.length}</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Filters */}
        {!loading && !error && (
          <div className="mb-8 rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
            <div className="mb-4 flex items-center gap-2">
              <MdFilterList className="h-5 w-5 text-gray-500" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-white">Filters</h3>
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Search */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">
                  Search {viewMode === 'product' ? 'Product Types' : 'Customers'}
                </label>
                <div className="flex items-center gap-2">
                  <MdSearch className="h-5 w-5 text-gray-500" />
                  <input
                    type="text"
                    placeholder={`Search ${viewMode === 'product' ? 'product types' : 'customers'}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="lg:col-span-2">
                <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Export Date Range</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
                    placeholder="Start Date"
                  />
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
                    placeholder="End Date"
                  />
                </div>
              </div>

              {/* Customer Filter */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Customer</label>
                <input
                  type="text"
                  placeholder="Filter by customer..."
                  value={filters.customer}
                  onChange={(e) => setFilters({...filters, customer: e.target.value})}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
                />
              </div>
            </div>

            {/* Clear Filters Button */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setFilters({startDate: '', endDate: '', customer: ''});
                  setSearchTerm('');
                }}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}

        {/* Data Table */}
        {!loading && !error && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-navy-700">
                  <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">
                    {viewMode === 'product' ? 'Product Type' : 'Customer'}
                  </th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Total Records</th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Total Quantity</th>
                  {viewMode === 'customer' && (
                    <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Product Types</th>
                  )}
                  <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Latest Export Date</th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentRecords.map((record) => (
                  <tr key={viewMode === 'product' ? record.product_type : record.customer_name} className="border-b border-gray-200 dark:border-navy-700">
                    <td className="py-4 text-sm font-medium text-navy-700 dark:text-white">
                      {viewMode === 'product' ? record.product_type : record.customer_name}
                    </td>
                    <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <MdInventory className="h-4 w-4 text-blue-500" />
                        <span>{record.total_records}</span>
                      </div>
                    </td>
                    <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <MdTrendingUp className="h-4 w-4 text-green-500" />
                        <span>{record.total_quantity.toLocaleString()} boxes</span>
                      </div>
                    </td>
                    {viewMode === 'customer' && (
                      <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-2">
                          <MdFactory className="h-4 w-4 text-purple-500" />
                          <span>{record.unique_product_types}</span>
                        </div>
                      </td>
                    )}
                    <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <MdCalendarToday className="h-4 w-4 text-purple-500" />
                        <span>{new Date(record.latest_export_date).toLocaleDateString()}</span>
                      </div>
                    </td>
                    <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => viewMode === 'product' ? handleViewProduct(record) : handleViewCustomer(record)}
                          className="rounded-lg bg-blue-50 p-2 text-blue-600 hover:bg-blue-100 dark:bg-navy-700 dark:text-blue-400 dark:hover:bg-navy-600"
                          title={`View ${viewMode === 'product' ? 'product' : 'customer'} details`}
                        >
                          <MdVisibility className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && !error && currentViewData.length > 0 && (
          <div className="mt-6 flex flex-col items-center justify-between gap-4 sm:flex-row">
            {/* Items per page selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Show:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="rounded-lg border border-gray-200 px-3 py-1 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-gray-600 dark:text-gray-400">entries</span>
            </div>

            {/* Page info */}
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {startIndex + 1} to {Math.min(endIndex, currentViewData.length)} of {currentViewData.length} entries
            </div>

            {/* Pagination buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:opacity-50 dark:border-navy-600"
              >
                <MdFirstPage className="h-4 w-4" />
                First
              </button>
              
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:opacity-50 dark:border-navy-600"
              >
                <MdChevronLeft className="h-4 w-4" />
                Previous
              </button>

              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {getPageNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === 'number' ? handlePageChange(page) : null}
                    disabled={page === '...'}
                    className={`flex h-8 w-8 items-center justify-center rounded-lg border text-sm ${
                      page === currentPage
                        ? 'border-blue-500 bg-blue-500 text-white'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-400 dark:hover:bg-navy-700'
                    } ${page === '...' ? 'cursor-default' : 'cursor-pointer'}`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:opacity-50 dark:border-navy-600"
              >
                Next
                <MdChevronRight className="h-4 w-4" />
              </button>
              
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:opacity-50 dark:border-navy-600"
              >
                Last
                <MdLastPage className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {!loading && !error && currentViewData.length === 0 && (
          <div className="mt-8 text-center">
            <MdInfo className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-lg font-medium text-gray-600 dark:text-gray-400">
              {exportRecords.length === 0 
                ? 'No export records found' 
                : `No ${viewMode === 'product' ? 'product types' : 'customers'} found matching your criteria`
              }
            </p>
          </div>
        )}
      </Card>

      {/* Product Details Modal */}
      {showProductDetails && selectedProductType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-2 sm:p-4">
          <div className="bg-white rounded-xl w-full max-w-7xl shadow-2xl dark:bg-navy-800 max-h-[95vh] overflow-hidden border border-gray-200 dark:border-navy-700">
            {/* Enhanced Modal Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-navy-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-navy-700 dark:to-navy-800 gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 dark:bg-navy-600 flex items-center justify-center">
                    <MdFactory className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                    {viewMode === 'product' ? selectedProductType.product_type : selectedProductType.customer_name}
                  </h3>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <MdInventory className="h-4 w-4 text-blue-500" />
                      {selectedProductType.total_records} records
                    </span>
                    <span className="flex items-center gap-1">
                      <MdTrendingUp className="h-4 w-4 text-green-500" />
                      {selectedProductType.total_quantity.toLocaleString()} boxes
                    </span>
                    {viewMode === 'customer' && (
                      <span className="flex items-center gap-1">
                        <MdFactory className="h-4 w-4 text-purple-500" />
                        {selectedProductType.unique_product_types} product types
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={fetchExportRecords}
                  disabled={loading}
                  className="flex items-center gap-1 sm:gap-2 rounded-lg bg-blue-500 px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50 transition-colors"
                  title="Refresh data"
                >
                  <MdRefresh className={`h-3 w-3 sm:h-4 sm:w-4 ${loading ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
                <button
                  onClick={closeProductDetails}
                  className="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors"
                  title="Close modal"
                >
                  <MdClose className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
            </div>

            {/* Enhanced Modal Body */}
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-180px)]">
              {/* Quick Stats Cards */}
              <div className={`grid grid-cols-1 gap-4 mb-6 ${viewMode === 'customer' ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-navy-700 dark:to-navy-600 rounded-lg p-4 border border-blue-200 dark:border-navy-600">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                      <MdInventory className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Records</p>
                      <p className="text-xl font-bold text-blue-900 dark:text-white">{selectedProductType.total_records}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-navy-700 dark:to-navy-600 rounded-lg p-4 border border-green-200 dark:border-navy-600">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                      <MdTrendingUp className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-700 dark:text-green-300">Total Quantity</p>
                      <p className="text-xl font-bold text-green-900 dark:text-white">{selectedProductType.total_quantity.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {viewMode === 'customer' && (
                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-navy-700 dark:to-navy-600 rounded-lg p-4 border border-purple-200 dark:border-navy-600">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center">
                        <MdFactory className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-purple-700 dark:text-purple-300">Product Types</p>
                        <p className="text-xl font-bold text-purple-900 dark:text-white">{selectedProductType.unique_product_types}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Enhanced Table Container */}
              <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-navy-700 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-50 dark:bg-navy-700">
                      <tr>
                        <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">ID</th>
                        <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Batch</th>
                        {viewMode === 'customer' && (
                          <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Product Type</th>
                        )}
                        <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Storage Date</th>
                        <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Quantity</th>
                        <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Location</th>
                        {viewMode === 'product' && (
                          <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Customer</th>
                        )}
                        <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Export Date</th>
                        <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-navy-800 divide-y divide-gray-200 dark:divide-navy-700">
                      {currentProductDetailsRecords.map((record, index) => (
                        <tr key={record.id} className={`hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors ${index % 2 === 0 ? 'bg-white dark:bg-navy-800' : 'bg-gray-50 dark:bg-navy-750'}`}>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-0.5 sm:px-2.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-navy-600 dark:text-blue-300">
                              #{record.id}
                            </span>
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{record.batch}</span>
                          </td>
                          {viewMode === 'customer' && (
                            <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              <div className="flex items-center gap-1 sm:gap-2">
                                <MdFactory className="h-3 w-3 sm:h-4 sm:w-4 text-indigo-500" />
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{record.product_type}</span>
                              </div>
                            </td>
                          )}
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <MdCalendarToday className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500" />
                              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{new Date(record.storage_date).toLocaleDateString()}</span>
                            </div>
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <MdInventory className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />
                              <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{record.quantity.toLocaleString()} boxes</span>
                            </div>
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <MdLocationOn className="h-3 w-3 sm:h-4 sm:w-4 text-orange-500" />
                              <div>
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{record.lane}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 block">Slot: {record.slot}</span>
                              </div>
                            </div>
                          </td>
                          {viewMode === 'product' && (
                            <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              <div className="flex items-center gap-1 sm:gap-2">
                                <MdFactory className="h-3 w-3 sm:h-4 sm:w-4 text-indigo-500" />
                                <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{record.customer_name || 'N/A'}</span>
                              </div>
                            </td>
                          )}
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <MdCalendarToday className="h-3 w-3 sm:h-4 sm:w-4 text-purple-500" />
                              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{new Date(record.export_date).toLocaleDateString()}</span>
                            </div>
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openDeleteConfirm(record)}
                                disabled={userRole === 'user' || deletingRecordId === record.id}
                                className={`rounded-lg p-1.5 sm:p-2 transition-all duration-200 ${
                                  userRole === 'user'
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-navy-600 dark:text-gray-500'
                                    : deletingRecordId === record.id
                                    ? 'bg-red-100 text-red-500 cursor-not-allowed dark:bg-navy-600 dark:text-red-400'
                                    : 'bg-red-50 text-red-600 hover:bg-red-100 hover:scale-105 dark:bg-navy-700 dark:text-red-400 dark:hover:bg-navy-600'
                                }`}
                                title={userRole === 'user' ? 'Delete not available for users' : deletingRecordId === record.id ? 'Deleting...' : 'Delete export record'}
                              >
                                {deletingRecordId === record.id ? (
                                  <div className="h-4 w-4 sm:h-5 sm:w-5 animate-spin rounded-full border-2 border-red-500 border-t-transparent"></div>
                                ) : (
                                  <MdDelete className="h-4 w-4 sm:h-5 sm:w-5" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Enhanced Pagination */}
              {selectedProductType.records.length > productDetailsItemsPerPage && (
                <div className="mt-6 bg-white dark:bg-navy-800 rounded-lg border border-gray-200 dark:border-navy-700 p-4">
                  <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Show:</span>
                      <select
                        value={productDetailsItemsPerPage}
                        onChange={(e) => setProductDetailsItemsPerPage(Number(e.target.value))}
                        className="rounded-lg border border-gray-200 px-2 sm:px-3 py-1 text-sm dark:border-navy-600 dark:bg-navy-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                      <span className="text-sm text-gray-600 dark:text-gray-400">entries</span>
                    </div>

                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 text-center">
                      Showing <span className="font-medium">{productDetailsStartIndex + 1}</span> to <span className="font-medium">{Math.min(productDetailsEndIndex, selectedProductType.records.length)}</span> of <span className="font-medium">{selectedProductType.records.length}</span> entries
                    </div>

                    <div className="flex items-center gap-1 sm:gap-2">
                      <button
                        onClick={() => setProductDetailsPage(1)}
                        disabled={productDetailsPage === 1}
                        className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 sm:px-3 py-2 text-xs sm:text-sm disabled:opacity-50 dark:border-navy-600 hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors"
                      >
                        <MdFirstPage className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">First</span>
                      </button>
                      
                      <button
                        onClick={() => setProductDetailsPage(productDetailsPage - 1)}
                        disabled={productDetailsPage === 1}
                        className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 sm:px-3 py-2 text-xs sm:text-sm disabled:opacity-50 dark:border-navy-600 hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors"
                      >
                        <MdChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">Previous</span>
                      </button>

                      <span className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-navy-700 rounded-lg">
                        {productDetailsPage}/{productDetailsTotalPages}
                      </span>

                      <button
                        onClick={() => setProductDetailsPage(productDetailsPage + 1)}
                        disabled={productDetailsPage === productDetailsTotalPages}
                        className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 sm:px-3 py-2 text-xs sm:text-sm disabled:opacity-50 dark:border-navy-600 hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors"
                      >
                        <span className="hidden sm:inline">Next</span>
                        <MdChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                      
                      <button
                        onClick={() => setProductDetailsPage(productDetailsTotalPages)}
                        disabled={productDetailsPage === productDetailsTotalPages}
                        className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 sm:px-3 py-2 text-xs sm:text-sm disabled:opacity-50 dark:border-navy-600 hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors"
                      >
                        <span className="hidden sm:inline">Last</span>
                        <MdLastPage className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* No Records Message */}
              {selectedProductType.records.length === 0 && (
                <div className="text-center py-12">
                  <MdInfo className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-lg font-medium text-gray-600 dark:text-gray-400">
                    No export records found for this product type
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl dark:bg-navy-800">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                  <MdWarning className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Delete Export Record
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Are you sure you want to delete this export record?
                </p>
              </div>
            </div>
            
            {recordToDelete && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg dark:bg-navy-700">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Batch:</span> {recordToDelete.batch}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Quantity:</span> {recordToDelete.quantity} boxes
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Customer:</span> {recordToDelete.customer_name || 'N/A'}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Export Date:</span> {new Date(recordToDelete.export_date).toLocaleDateString()}
                </p>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeDeleteConfirm}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-navy-700 dark:text-gray-300 dark:border-navy-600 dark:hover:bg-navy-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportRecords; 